import React, { useState, useEffect } from 'react';
import { MonitorPlay, Users, Clock, ChevronDown, Sparkles, Play, Info } from 'lucide-react';
import { fetchWithAuth } from '../../utils/api';

export const KioskQueueTab = ({ org, onClose }) => {
  const [appointments, setAppointments] = useState([]);
  const [offers, setOffers] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [displays, setDisplays] = useState([{ id: 'local', label: 'Local Display' }]);
  const [selectedDisplay, setSelectedDisplay] = useState('local');
  const [detecting, setDetecting] = useState(false);
  const [screenDetailsObj, setScreenDetailsObj] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const detectDisplays = async () => {
    setDetecting(true);
    if ('getScreenDetails' in window) {
      try {
        const screenDetails = await window.getScreenDetails();
        setScreenDetailsObj(screenDetails);
        const updateScreens = () => {
          const screens = screenDetails.screens
            .filter(s => !s.isPrimary)
            .map((s, idx) => ({
              id: s.label || `External Display ${idx + 1}`,
              label: s.label || `External Display ${idx + 1}`,
              screen: s
            }));
          setDisplays([{ id: 'local', label: 'Local Display' }, ...screens]);
        };
        updateScreens();
        screenDetails.addEventListener('screenschange', updateScreens);
      } catch (err) {
        console.warn('Screen details API permission denied or error:', err);
        alert('Please allow Window Management permissions in your browser to detect HDMI displays.');
      }
    } else {
      alert('Your browser does not support multi-screen detection. Please use Chrome or Edge.');
    }
    setDetecting(false);
  };

  const castToDisplay = () => {
    let features = 'popup=yes,fullscreen=yes';
    
    if (selectedDisplay !== 'local') {
      const displayObj = displays.find(d => d.id === selectedDisplay);
      if (displayObj && displayObj.screen) {
        const s = displayObj.screen;
        const left = s.availLeft !== undefined ? s.availLeft : s.left;
        const top = s.availTop !== undefined ? s.availTop : s.top;
        const width = s.availWidth !== undefined ? s.availWidth : s.width;
        const height = s.availHeight !== undefined ? s.availHeight : s.height;
        features = `left=${left},top=${top},width=${width},height=${height},popup=yes,fullscreen=yes`;
      }
    }

    const castUrl = new URL(window.location.href);
    castUrl.searchParams.set('tv', 'true');
    
    const newWin = window.open(castUrl.toString(), '_blank', features);
    if (newWin) {
      if (selectedDisplay !== 'local') {
        const displayObj = displays.find(d => d.id === selectedDisplay);
        if (displayObj && displayObj.screen) {
          const s = displayObj.screen;
          const left = s.availLeft !== undefined ? s.availLeft : s.left;
          const top = s.availTop !== undefined ? s.availTop : s.top;
          const width = s.availWidth !== undefined ? s.availWidth : s.width;
          const height = s.availHeight !== undefined ? s.availHeight : s.height;
          setTimeout(() => {
            newWin.moveTo(left, top);
            newWin.resizeTo(width, height);
          }, 100);
        }
      }
    } else {
      alert('Popup Blocked! Please allow pop-ups to cast.');
    }
  };



  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchQueue = async () => {
      try {
        const res = await fetchWithAuth('/appointments');
        if (res.ok) {
          const data = await res.json();
          const todayStr = new Date().toISOString().split('T')[0];
          const active = data.filter(a => a.date.startsWith(todayStr) && ['checked-in', 'started'].includes(a.status));
          setAppointments(active);
        }
      } catch (err) {
        console.error(err);
      }
    };
    
    fetchQueue();
    const poll = setInterval(fetchQueue, 10000);
    return () => clearInterval(poll);
  }, []);

  useEffect(() => {
    const fetchOffers = async () => {
      if (!org?.id) return;
      try {
        const res = await fetchWithAuth('/offers', { headers: { 'x-org-id': org.id } });
        if (res.ok) {
          const data = await res.json();
          const active = data.filter(o => o.status !== 'Inactive');
          setOffers(active);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchOffers();
    const poll = setInterval(fetchOffers, 60000);
    return () => clearInterval(poll);
  }, [org?.id]);

  const realCheckedIn = appointments.filter(a => a.status === 'checked-in');
  
  // Generating 20 dummy appointments for UI demonstration
  const activeAppointments = Array.from({ length: 20 }, (_, i) => {
    const baseHour = 10 + Math.floor((i * 15) / 60);
    const mins = (i * 15) % 60;
    const ampm = baseHour >= 12 ? 'PM' : 'AM';
    const displayHour = baseHour > 12 ? baseHour - 12 : baseHour;
    const timeStr = `${displayHour}:${mins.toString().padStart(2, '0')} ${ampm}`;
    return {
      id: `d${i}`,
      status: 'checked-in',
      time: timeStr,
      customerName: `User ${i + 1}`,
      token: `T-${i + 1}`
    };
  });
  
  const activeOffers = offers;

  return (
    <div className="absolute inset-0 bg-[#faf9f6] text-slate-900 flex flex-col overflow-hidden font-sans">
      
      {/* Background Theme Elements */}
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-bl from-[var(--theme-primary)]/10 to-transparent rounded-full blur-[100px] -z-10 translate-x-1/4 -translate-y-1/4 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-[var(--theme-primary)]/5 to-transparent rounded-full blur-[80px] -z-10 -translate-x-1/4 translate-y-1/4 pointer-events-none"></div>
      
      {/* Header */}
      <header className="px-10 py-5 flex items-center justify-between border-b border-slate-200/60 bg-white/80 backdrop-blur-2xl shadow-sm z-50 flex-shrink-0 relative">
        <div className="flex items-center gap-5">
          <div style={{ fontFamily: "'Inter', system-ui, sans-serif" }} className="flex flex-col justify-center">
            <h1 className="text-4xl font-bold text-slate-900 tracking-tight leading-none uppercase">
              {org?.name || 'Green Trends'}
            </h1>
          </div>
        </div>
        
        <div className="flex items-center gap-6">
          {!new URLSearchParams(window.location.search).get('tv') && (
            <div className="flex items-center gap-3 bg-slate-50 p-1 rounded-none border border-slate-200 shadow-inner">
              <div className="relative flex items-center group min-w-[150px]">
                <button 
                  onClick={() => {
                    if (!isDropdownOpen) detectDisplays();
                    setIsDropdownOpen(!isDropdownOpen);
                  }}
                  className="flex items-center justify-between bg-white hover:bg-slate-50 rounded-none border border-slate-200 px-3 py-1.5 text-[10px] font-bold text-slate-700 uppercase tracking-widest outline-none cursor-pointer w-full transition-all shadow-sm"
                >
                  <div className="flex items-center gap-2">
                    <span className="truncate max-w-[120px]">
                      {selectedDisplay === 'local' ? 'Local Display' : (displays.find(d => d.id === selectedDisplay)?.label || 'Display')}
                    </span>
                  </div>
                  <ChevronDown size={14} className={`text-slate-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {isDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsDropdownOpen(false)}></div>
                    <div className="absolute top-[calc(100%+8px)] left-0 w-full bg-white rounded-none shadow-xl border border-slate-200 z-50 py-2 overflow-hidden">
                      {displays.map(d => (
                        <button
                          key={d.id}
                          onClick={() => {
                            setSelectedDisplay(d.id);
                            setIsDropdownOpen(false);
                          }}
                          className={`w-full text-left px-4 py-2.5 text-[11px] font-bold uppercase tracking-widest transition-colors flex items-center gap-2 ${
                            selectedDisplay === d.id 
                              ? 'bg-[var(--theme-primary)]/10 text-[var(--theme-primary)] border-l-2 border-[var(--theme-primary)]' 
                              : 'text-slate-600 hover:bg-slate-50 border-l-2 border-transparent'
                          }`}
                        >
                          <span className="truncate block">{d.label}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <button 
                onClick={castToDisplay}
                className="flex items-center justify-center gap-2 px-4 py-1.5 bg-[var(--theme-primary)] text-white rounded-none font-bold text-[10px] uppercase tracking-widest hover:brightness-110 transition-all shadow-md"
              >
                Cast
              </button>
            </div>
          )}

          <div className="w-[1px] h-10 bg-slate-200 mx-2"></div>

          <div className="text-right flex flex-col items-end justify-center mr-2">
            <p className="text-[11px] text-[var(--theme-primary)] font-bold tracking-[0.1em] uppercase mb-0.5">
              {currentTime.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>
            <h2 className="text-3xl font-bold tracking-tighter text-slate-900 leading-none tabular-nums">
              {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </h2>
          </div>
          
          {new URLSearchParams(window.location.search).get('tv') && (
            <button 
              onClick={() => {
                if (!document.fullscreenElement) {
                  document.documentElement.requestFullscreen().catch(err => console.log(err));
                } else {
                  document.exitFullscreen();
                }
              }} 
              className="px-5 py-2 bg-[var(--theme-primary)] hover:brightness-110 text-white rounded-none transition-all font-bold text-[11px] uppercase tracking-widest shadow-sm ml-4"
            >
              Full Screen
            </button>
          )}
          
          {onClose && (
            <button 
              onClick={onClose} 
              className="px-5 py-2 bg-white hover:bg-red-50 text-red-600 rounded-none transition-all font-bold text-[11px] uppercase tracking-widest border border-slate-200 hover:border-red-200 shadow-sm ml-2"
            >
              Exit
            </button>
          )}
        </div>
      </header>

      {/* Main Grid */}
      <main className="flex-1 min-h-0 flex flex-col p-4 lg:p-6 gap-4 overflow-hidden z-10 max-w-[1920px] mx-auto w-full">
        
        {/* Waiting Column */}
        <section className="bg-white rounded-md border border-slate-200 shadow-sm p-6 flex flex-col h-full overflow-hidden relative group">
          <header className="flex items-center justify-between mb-8 pb-6 border-b border-slate-100 flex-shrink-0 relative z-10" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
            <div className="flex items-center gap-4">
              <h3 className="text-sm font-bold flex items-center gap-3 text-slate-500 uppercase tracking-[0.2em]">
                Waiting List
              </h3>
            </div>
            <div className="text-slate-400 font-bold text-4xl leading-none">
              {activeAppointments.length} <span className="text-sm font-bold text-slate-300">Total</span>
            </div>
          </header>
          
          <div className="flex-1 min-h-0 overflow-y-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 auto-rows-max gap-6 pr-4 pb-8 custom-scrollbar relative z-10">
            {activeAppointments.map((apt, index) => {
              const tokenStr = apt.token || `T-${String(index + 1).padStart(3, '0')}`;
              const waitTime = apt.time || '10 Mins';
              const rawName = apt.customerName || `Customer ${index + 1}`;
              
              const maskName = (name) => {
                if (!name) return '';
                return name.split(' ').map(part => {
                  if (part.length <= 1) return part;
                  if (part.length <= 2) return part[0] + '*';
                  return part[0] + '*'.repeat(part.length - 2) + part[part.length - 1];
                }).join(' ');
              };
              
              const maskedName = rawName.startsWith('User ') ? rawName : maskName(rawName);
              
              return (
              <div key={apt.id} className="bg-white rounded border border-slate-200 shadow-sm flex flex-col shrink-0 relative transition-all duration-300 hover:shadow-md hover:border-purple-300 group/card overflow-hidden">
                {/* Top Section: Token */}
                <div className="bg-[#f2ebf0] px-4 py-2 border-b border-slate-100 flex items-center">
                  <span className="text-[11px] font-bold text-[#5e2b4f] uppercase tracking-wider">
                    TOKEN #{tokenStr.replace('T-', '').replace('A-', '').replace('B-', '').replace('C-', '')}
                  </span>
                </div>
                
                {/* Bottom Section: Customer & Wait Time */}
                <div className="px-4 py-3 flex items-center justify-between relative z-10 bg-white">
                  <div className="flex flex-col min-w-0 pr-4 border-r border-slate-100 flex-1">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Customer</p>
                    <h4 className="text-base font-bold text-slate-800 leading-tight truncate">{maskedName}</h4>
                  </div>
                  
                  <div className="flex flex-col items-end pl-4 shrink-0">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Wait Time</p>
                    <p className="text-base font-bold text-[#5e2b4f] tracking-tight">{waitTime}</p>
                  </div>
                </div>
              </div>
            )})}
            {activeAppointments.length === 0 && (
              <div className="col-span-full flex-1 flex flex-col items-center justify-center text-slate-400 py-24 border-2 border-dashed border-slate-200 bg-slate-50/50 rounded-2xl">
                <Users size={56} className="text-slate-300 mb-5" />
                <p className="text-2xl font-bold text-slate-600 tracking-tight">Queue is empty</p>
                <p className="text-sm font-medium mt-2 text-slate-400">No customers are currently waiting</p>
              </div>
            )}
          </div>
        </section>

      </main>
    </div>
  );
};


